import React from 'react';
import DOMPurify, { type Config as DOMPurifyConfig } from 'dompurify';
import { useTranslation } from 'localization';
import SphereAvatar from '../../molecules/sphere-avatar/sphere-avatar';
import { useSphereInfo } from '../../../hooks/useSphereInfo';
import type { GlyphColor } from '../../../types/glyph';
import type { ApiMessage } from '../../molecules/SphereChatPanel/UseConversation';
import './ChatMessageCardAtom.scss';

export interface ChatMessageCardAtomProps {
  message: ApiMessage;
  onClick?: (message: ApiMessage) => void;
  className?: string;
}

const PURIFY_CONFIG: DOMPurifyConfig = {
  ALLOWED_TAGS: ['a', 'b', 'strong', 'i', 'em', 'u', 's', 'p', 'br', 'ul', 'ol', 'li', 'pre', 'code', 'blockquote', 'span', 'img'],
  ALLOWED_ATTR: ['href', 'target', 'rel', 'style', 'src', 'alt', 'title', 'width', 'height', 'class', 'data-mention', 'data-email', 'data-user-id', 'data-image-id'],
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|data:image\/(?:png|jpeg|gif|webp|svg\+xml);base64,)/i,
};

function sanitize(html: string): string {
  return DOMPurify.sanitize(html, PURIFY_CONFIG);
}

function formatDate(dateString: string, t: (key: string, opts?: Record<string, string>) => string): string {
  const messageDate = new Date(dateString);
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - messageDate.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  const todayStr = now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayStr = yesterday.toDateString();
  const messageDayStr = messageDate.toDateString();

  const timeString = messageDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  if (messageDayStr === todayStr) return t('chatMessageCard.time.today', { time: timeString });
  if (messageDayStr === yesterdayStr) return t('chatMessageCard.time.yesterday', { time: timeString });
  if (diffDays <= 7) return `${messageDate.toLocaleDateString([], { weekday: 'long' })} ${timeString}`;
  if (diffDays <= 365) return `${messageDate.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${timeString}`;
  return `${messageDate.toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' })} ${timeString}`;
}

const AuthorBadge: React.FC<{ authorSphereId: string; createdAt: string; edited?: boolean }> = ({ authorSphereId, createdAt, edited }) => {
  const { t } = useTranslation();
  const { info, loading } = useSphereInfo(authorSphereId);
  const sphere = (info as any)?.sphere ?? (info as any);
  const props = sphere?.properties ?? {};
  const name = props.name || sphere?.label || authorSphereId || t('chatMessageCard.author.defaultName');
  const glyph = (sphere?.glyph ?? props.glyph) as string | undefined;
  const glyphColor = (sphere?.glyphColor ?? props.glyphColor) as GlyphColor | undefined;

  return (
    <>
      <div className="chat-message-card__avatar">
        <SphereAvatar
          glyph={glyph}
          glyphColor={glyphColor}
          sphereId={authorSphereId}
          sphereName={name}
          imageId={props.imageId}
          size="small"
          showInitials
        />
      </div>
      <div className="chat-message-card__meta">
        <span className="chat-message-card__author">{loading ? t('chatMessageCard.author.loading') : name}</span>
        <span className="chat-message-card__timestamp">{formatDate(createdAt, t)}</span>
        {edited && <span className="chat-message-card__edited">{t('chatMessageCard.edited')}</span>}
      </div>
    </>
  );
};

const ChatMessageCardAtom: React.FC<ChatMessageCardAtomProps> = ({ message, onClick, className = '' }) => {
  const handleClick = () => onClick?.(message);

  return (
    <article
      className={`chat-message-card ${className}`}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={handleClick}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(); } } : undefined}
    >
      <div className="chat-message-card__header">
        <i className="pi pi-comments chat-message-card__type-icon" />
        <AuthorBadge authorSphereId={message.author_id} createdAt={message.created_at} edited={message.edited} />
      </div>
      <div className="chat-message-card__content" dangerouslySetInnerHTML={{ __html: sanitize(message.content) }} />
    </article>
  );
};

export default ChatMessageCardAtom;
